import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import MorseBeacon from "../../../../components/intro/MorseBeacon";
import { trackError } from "../../../../lib/metrics";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { Modal } from "../../../../components/ui/ModalRegister";
import { TechHintBeacon } from "../../../origami/components/ui/TechHintBeacon";
import { CABLE_SOLUTION } from "../../../../data/origami/cableSolution";
import { useToast } from "../../../../components/ui/Toast";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { getInspectKeys } from "../../../../data/inspectables";
import OrigamiCardsPuzzle from "../../../origami/components/puzzles/OrigamiCardsPuzzle";
import HintsModalHub from "../../../../components/ui/HintsModalHub";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import OrigamiCommunicationModal from "../../../origami/components/ui/OrigamiCommunicationModal";
import { useOrigamiReboot } from "../../../../hooks/useOrigamiReboot";
import { rebootText } from "../../../../data/origami/origamiTexts";

const API = process.env.REACT_APP_API_URL;

/**
 * Stage 1 — Joueur 1 — Composant partagé (4J / 5J / 6J)
 *
 * Props spécifiques à la variante :
 *   playerCount {number}  — 4, 5 ou 6. Détermine :
 *     - getInspectKeys({ playerCount })
 *     - pushInfraState (appelé uniquement si playerCount > 4)
 *     - missionResolve key : "s1.p4.drawer.opened" (4J) vs "s1.p5.drawer.opened" (5J/6J)
 */
export default function Stage1Player1Room({
  code,
  playerId,
  hintSignal,
  raisedCount,
  raisedPlayers,
  centerMaxH,
  playerCount,
  pseudo,
  syncStartedAt,
  syncResetCounter,
  serverNow,
  onMorseUpdated,
}) {
  const INSPECT_KEYS = useMemo(() => {
    return getInspectKeys({
      gameId: 1,
      playerCount,
      stage: 1,
      seat: 1,
    });
  }, [playerCount]);

  const partialMilestoneShownRef = useRef(false);

  // — Modales —
  const [showRackModal, setShowRackModal] = useState(false);
  const [showEtiquette1Modal, setShowEtiquette1Modal] = useState(false);
  const [showEtiquette2Modal, setShowEtiquette2Modal] = useState(false);
  const [showCables1Modal, setShowCables1Modal] = useState(false);
  const [showCables2Modal, setShowCables2Modal] = useState(false);
  const [showPostIt1Modal, setShowPostIt1Modal] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showOrigamiCardsModal, setShowOrigamiCardsModal] = useState(false);

  // — États câbles —
  const [server1Cables, setServer1Cables] = useState({ A: false, B: false, C: false });
  const [server2Cables, setServer2Cables] = useState({ A: false, B: false, C: false, D: false });
  const [serverPrincipalCables, setServerPrincipalCables] = useState({
    A: false, B: false, C: false, D: false, E: false, F: false, G: false, H: false, I: false,
  });
  const [server4Cables, setServer4Cables] = useState({ A: true, B: false });
  const [cablesFeedback, setCablesFeedback] = useState("");
  const [cablesSolved, setCablesSolved] = useState(false);
  const [partialSolved, setPartialSolved] = useState(false);
  const [morseResetTick, setMorseResetTick] = useState(0); // eslint-disable-line no-unused-vars

  // — États puzzle —
  const [origamiCardsSolved, setOrigamiCardsSolved] = useState(false);
  const [bgReady, setBgReady] = useState(false);

  // — Hooks —
  const toast = useToast();
  const { unlockScenarioStep } = useScenarioTracker({ code, stage: 1 });
  const { inspectedCount, total } = useInspectProgress({
    sessionCode: code,
    stage: 1,
    playerId: Number(playerId),
    keys: INSPECT_KEYS,
  });
  const { showOrigamiReboot, setShowOrigamiReboot } = useOrigamiReboot({ code, playerId });
  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 1 });

  const closeHints = useCallback(() => setShowHintModal(false), []);
  const usedStorageKey = useMemo(() => `hints_used:${code}:s1:p${playerId}`, [code, playerId]);

  // — Dérivés —
  const lockRoomUI = cablesSolved && origamiCardsSolved;
  const backgroundUrl = "/assets/s1-p1-room.webp";

  // — Clé mission drawer : dépend du playerCount —
  // En 4J le tiroir rapatrié est chez J4, en 5J/6J il est chez J5
  const drawerMissionKey = playerCount === 4 ? "s1.p4.drawer.opened" : "s1.p5.drawer.opened";

  // — Assets —
  const rackCentral = "/assets/rackCentral.webp";
  const cables1 = "/assets/Cables1.webp";
  const cables2 = "/assets/Cables2.webp";
  const etiquette1 = "/assets/etiquette1.png";
  const etiquette2 = "/assets/etiquette2.png";
  const postIt1 = "/assets/postit1.webp";
  const origamiPaperImg = "/assets/p1_paper.png";

  useEffect(() => { setBgReady(false); }, [backgroundUrl]);
  useEffect(() => { if (!hintSignal) return; setShowHintModal(true); }, [hintSignal]);

  // — API helpers —
  async function resetMorseServer() {
    const res = await fetch(`${API}/api/sessions/${code}/morse/reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ playerId: Number(playerId) }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || "reset failed");
    }
    return res.json();
  }

  // pushInfraState n'existe qu'en 5J/6J (tiroir J5 lit l'état infra en temps réel)
  async function pushInfraState() {
    if (playerCount <= 4) return;
    try {
      await fetch(`${API}/api/sessions/${code}/infra-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          playerId: Number(playerId),
          server1: server1Cables,
          server2: server2Cables,
          serverPrincipal: serverPrincipalCables,
          server4: server4Cables,
        }),
      });
    } catch (e) {
      console.error("pushInfraState failed", e);
    }
  }

  // — Logique câbles —
  function toggleCable(server, name) {
    if (server === "server1") setServer1Cables((p) => ({ ...p, [name]: !p[name] }));
    else if (server === "server2") setServer2Cables((p) => ({ ...p, [name]: !p[name] }));
    else if (server === "serverPrincipal") setServerPrincipalCables((p) => ({ ...p, [name]: !p[name] }));
    else if (server === "server4") setServer4Cables((p) => ({ ...p, [name]: !p[name] }));
    setCablesFeedback("");
  }

  function isServerCorrect(serverState, solutionState) {
    return Object.keys(solutionState).every((key) => serverState[key] === solutionState[key]);
  }

  function getServerStateByKey(key) {
    if (key === "server1") return server1Cables;
    if (key === "server2") return server2Cables;
    if (key === "serverPrincipal") return serverPrincipalCables;
    if (key === "server4") return server4Cables;
    return null;
  }

  function getTotalActivePorts() {
    return [
      ...Object.values(server1Cables),
      ...Object.values(server2Cables),
      ...Object.values(serverPrincipalCables),
      ...Object.values(server4Cables),
    ].filter(Boolean).length;
  }

  function computeGlobalCablesStatus() {
    const s1Ok = isServerCorrect(server1Cables, CABLE_SOLUTION.server1);
    const s2Ok = isServerCorrect(server2Cables, CABLE_SOLUTION.server2);
    const spOk = isServerCorrect(serverPrincipalCables, CABLE_SOLUTION.serverPrincipal);
    const s4Ok = isServerCorrect(server4Cables, CABLE_SOLUTION.server4);
    const ok = s1Ok && s2Ok && spOk && s4Ok;
    const correctCount = [s1Ok, s2Ok, spOk, s4Ok].filter(Boolean).length;
    const totalActive = getTotalActivePorts();
    return { ok, correctCount, totalActive };
  }

  async function checkRackCables(rackKey) {
    const state = getServerStateByKey(rackKey);
    const solution = CABLE_SOLUTION[rackKey];
    if (!state || !solution) return;

    const rackOk = isServerCorrect(state, solution);

    if (!rackOk) {
      trackError({ sessionCode: code, stage: 1, playerId, reason: "stage1_cables_wrong_rack", meta: { rack: rackKey } });
      setCablesSolved(false);
      setCablesFeedback("❌ Configuration incorrecte : ce rack ne peut pas démarrer avec ces branchements.");
      return;
    }

    const global = computeGlobalCablesStatus();

    if (global.correctCount < 2 && partialSolved) setPartialSolved(false);

    if (global.ok) {
      setCablesFeedback("La combinaison est correcte… Le serveur se relance en mode urgence, Origami a l'air de vouloir communiquer avec vous.");
      setCablesSolved(true);
      unlockScenarioStep("s1.servers.rebooted");

      try {
        const data = await resetMorseServer();
        onMorseUpdated?.(data);
      } catch (e) {
        console.error("morse reset failed", e);
      }

      try {
        await fetch(`${API}/api/sessions/${code}/origami-reboot`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ playerId: Number(playerId) }),
        });
      } catch (e) {
        console.error("origami reboot failed", e);
      }

      setPartialSolved(false);
      missionUnlock({ key: "s1.p1.morseCode.opened", text: `${pseudo} a rebranché les serveurs. Une lumière commence à clignoter.` });
      missionResolve({ key: drawerMissionKey });
      return;
    }

    if (global.correctCount >= 2) {
      if (!partialMilestoneShownRef.current) {
        partialMilestoneShownRef.current = true;
        setPartialSolved(true);
        setCablesSolved(false);
        setCablesFeedback("Une partie du rack se réveille : quelques voyants se stabilisent, mais les sondes énergie affichent encore une configuration instable.");
        return;
      }
      setPartialSolved(true);
      setCablesSolved(false);
      setCablesFeedback("✅ Branchement valide pour ce rack… mais ce n'est pas encore suffisant pour relancer le serveur. Vérifiez les autres racks.");
      return;
    }

    if (global.totalActive > 10) {
      setPartialSolved(false);
      setCablesSolved(false);
      setCablesFeedback("Ce rack est bien configuré, mais l'alimentation globale n'est pas stable : « Surconsommation détectée — merci de limiter aux systèmes critiques ».");
      return;
    }

    setPartialSolved(false);
    setCablesSolved(false);
    setCablesFeedback("✅ Branchement valide pour ce rack… mais ce n'est pas encore suffisant pour relancer le serveur. Vérifiez les autres racks.");
  }

  // Helper : tester + push infra si besoin
  async function handleTestRack(rackKey) {
    checkRackCables(rackKey);
    await pushInfraState();
  }

  // — Handlers d'ouverture —
  const handleToggleServer = () => { setCablesFeedback(""); setShowRackModal(true); };
  const handleToggleEtiquette1 = () => { setCablesFeedback(""); setShowEtiquette1Modal((p) => !p); };
  const handleToggleEtiquette2 = () => { setCablesFeedback(""); setShowEtiquette2Modal((p) => !p); };
  const handleToggleCables1 = () => { setCablesFeedback(""); setShowCables1Modal((p) => !p); };
  const handleToggleCables2 = () => { setCablesFeedback(""); setShowCables2Modal((p) => !p); };
  const handleTogglePostIt1 = () => setShowPostIt1Modal((p) => !p);

  // — Helper rendu bouton port —
  function PortButton({ server, name, active }) {
    return (
      <button
        type="button"
        onClick={() => toggleCable(server, name)}
        className={`flex items-center justify-between rounded-md px-3 py-2 text-xs border transition ${
          active ? "border-green-400 bg-green-600/30 text-green-100" : "border-gray-600 bg-gray-800 text-gray-100"
        }`}
      >
        <span>Port {name}</span>
        <span>{active ? "🔌 connecté" : "⚪ débranché"}</span>
      </button>
    );
  }

  function CablesFeedbackLine() {
    if (!cablesFeedback) return null;
    return (
      <p className={`mt-2 text-xs ${cablesSolved ? "text-green-300" : "text-amber-200"}`}>
        {cablesFeedback}
      </p>
    );
  }

  // — Hints —
  const hintCategories = useMemo(() => [
    {
      id: "cabling",
      title: "Câblage — Mode urgence",
      subtitle: "Branchements des racks / sobriété",
      hintKeyPrefix: "s1:p1:cabling",
      levels: {
        l1: (<><p className="mb-2">Ces câbles n'ont pas poussé tout seuls : quelqu'un a forcément documenté ce branchement.</p><p className="text-xs text-gray-300">La note de l'équipe Green IT parle de <span className="italic">config minimale</span> : ne rallume que ce qui est utile.</p></>),
        l2: (<><p className="mb-2">Tu n'as pas besoin de tout remettre : l'objectif est un "mode urgence" avec seulement l'essentiel.</p><p className="text-xs text-gray-300">Observe le post-it : il décrit précisément quoi garder branché pour stabiliser le rack.</p></>),
        l3: (<><p className="mb-2">Inspecte bien les etiquettes jaunes des serveurs et les cables.</p><p className="text-xs text-gray-300">Les branchements finaux sont disponibles dans le tiroir de la salle technique.</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Branchements)</p><ul className="mt-2 text-xs text-gray-200 list-disc pl-5 space-y-1"><li>Serveur 1 (Application) : ports <span className="font-mono">A, C</span> (B hors-ligne)</li><li>Serveur 2 (Base de données) : ports <span className="font-mono">B, C</span> (A &amp; D rechange)</li><li>Rack principal : ports <span className="font-mono">B, C, H, I</span></li><li>Bloc auxiliaire (Sauvegardes) : ports <span className="font-mono">A, B</span> (ne pas débrancher)</li></ul></>),
      },
    },
    {
      id: "terminal",
      title: "Terminal",
      subtitle: "Ordre des formes",
      hintKeyPrefix: "s1:p1:terminal",
      levels: {
        l1: (<><p className="mb-2">Un seul des cotés des tuiles est valide.</p><p className="text-xs text-gray-300">Quel côté pourrait être le bon ?</p></>),
        l2: (<><p>Repère les 4 formes qui ont la même couleur et le même remplissage.</p></>),
        l3: (<><p className="mb-2">Pour l'ordre, il s'agit d'une suite logique croissante.</p><p className="text-xs text-gray-300">Qu'est ce qui augmente de forme en forme?</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Formes)</p><p className="mt-2 text-xs text-gray-200">Le nombre de côtés augmente de 1 de forme en forme.</p><p className="mt-2 text-xs text-gray-200">Ordre : <span className="font-bold text-amber-200">Triangle (blanc), Carré (blanc), Pentagone (blanc), Hexagone (blanc)</span></p></>),
      },
    },
    {
      id: "morse",
      title: "Signal Morse",
      subtitle: "Traduire le message clignotant",
      hintKeyPrefix: "s1:p1:morse",
      levels: {
        l1: (<><p className="mb-2">Le serveur est réveillé… la lumière n'est plus aléatoire : c'est régulier, comme du morse.</p><p className="text-xs text-gray-300">Cherche un motif court/long. Un autre joueur peut t'aider à le traduire.</p></>),
        l2: (<><p className="mb-2">Le morse se lit en "impulsions" : courtes et longues, séparées par des pauses.</p><p className="text-xs text-gray-300">Note une séquence (ex: . . - - .) puis compare avec un alphabet morse.</p></>),
        l3: (<><p className="mb-2">Un alphabet morse est sans doute disponible dans le bureau de Samir.</p><p className="text-xs text-gray-300">Le message semble être une anagramme du mot "Origami"</p></>),
        answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Morse)</p><p className="mt-2 text-xs text-gray-200">Mot de passe : <span className="font-bold text-amber-200">Miragio</span></p></>),
      },
    },
  ], []);

  // — Rendu —
  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div
        className="relative h-screen w-auto mx-auto aspect-square"
        style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}
      >
        {/* HUD top-right */}
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>

        {/* Badge salle + mode éco */}
        <div className="absolute left-4 top-4 flex">
          <div className="relative rounded-full bg-black/70 border border-white/15 mr-2 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
            Salle des serveurs
          </div>

          {cablesSolved && (
            <div className="relative z-10 flex items-center gap-2">
              <div className="rounded-full bg-emerald-900/80 border border-emerald-400/70 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-emerald-100 shadow-[0_0_12px_rgba(16,185,129,0.6)]">
                Mode éco
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const data = await resetMorseServer();
                    onMorseUpdated?.(data);
                    toast.success("Signal relancé.", { title: "Morse" });
                  } catch (e) {
                    toast.error("Impossible de relancer le morse.", { title: "Morse" });
                  }
                }}
                className="rounded-full bg-cyan-900/80 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] hover:bg-white/15 uppercase text-cyan-100 transition"
                title="Relancer le signal depuis le début"
              >
                ↻ Relancer le signal
              </button>
            </div>
          )}
        </div>

        {/* Fond */}
        <img
          src={backgroundUrl}
          alt="Salle serveur"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)}
          onError={() => setBgReady(true)}
        />

        {/* Hotspots */}
        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>
          {!cablesSolved && (
            <img src={rackCentral} alt="Rack central"
              onClick={() => { markInspectedOnce('stage1player1:rackCentral'); handleToggleServer(); }}
              className="rackCentral absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}

          {partialSolved && !cablesSolved && <TechHintBeacon playerCount={playerCount} />}

          {cablesSolved && (
            <MorseBeacon
              active={true}
              syncStartedAt={syncStartedAt}
              syncResetCounter={syncResetCounter || 0}
              serverNow={serverNow}
              resetSignal={morseResetTick}
              className="left-[42.7%] top-[20.5%]"
            />
          )}

          {!cablesSolved && (
            <img src={etiquette1} alt="étiquette1"
              onClick={() => { markInspectedOnce('stage1player1:etiquette1'); handleToggleEtiquette1(); }}
              className="etiquette1 absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}

          {!origamiCardsSolved && (
            <img src={origamiPaperImg} alt="Terminal de supervision"
              onClick={() => { markInspectedOnce("stage1player1:origamiPaper"); setShowOrigamiCardsModal(true); }}
              className="p1-origami-terminal absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}

          {!cablesSolved && (
            <img src={etiquette2} alt="étiquette2"
              onClick={() => { markInspectedOnce('stage1player1:etiquette2'); handleToggleEtiquette2(); }}
              className="etiquette2 absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}

          {!cablesSolved && (
            <img src={cables1} alt="cables1"
              onClick={() => { markInspectedOnce('stage1player1:cables1'); handleToggleCables1(); }}
              className="cables1 absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}

          {!cablesSolved && (
            <img src={cables2} alt="cables2"
              onClick={() => { markInspectedOnce('stage1player1:cables2'); handleToggleCables2(); }}
              className="cables2 absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}

          {!lockRoomUI && (
            <img src={postIt1} alt="postit1"
              onClick={() => { markInspectedOnce('stage1player1:postIt1'); handleTogglePostIt1(); }}
              className="postit1 absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          )}
        </div>
      </div>

      {/* === MODALES === */}

      {/* Rack principal */}
      <Modal open={showRackModal} title="Rack principal — HORS LIGNE" onClose={() => setShowRackModal(false)} onHint={() => setShowHintModal(true)}>
        <p>Les câbles de ce rack sont tous débranchés. Si tu rallumes tout d&apos;un coup, tu risques surtout de faire exploser la conso plutôt que de sauver Origami.</p>
        <p className="text-sm text-gray-300">La doc interne mentionne un « mode urgence » : ne garder que les serveurs essentiels pour limiter l&apos;impact énergétique tout en maintenant le service critique.</p>
        <div className="mt-3 rounded-lg bg-black/60 border border-white/10 px-3 py-3">
          <p className="text-xs text-gray-200">Schéma simplifié — coche les câbles que tu choisis de rebrancher, puis clique sur <span className="font-semibold">Tester le branchement</span>.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {["A", "B", "C", "D", "E", "F", "G", "H", "I"].map((name) => (
              <PortButton key={name} server="serverPrincipal" name={name} active={serverPrincipalCables[name]} />
            ))}
          </div>
          <button type="button" onClick={() => handleTestRack("serverPrincipal")}
            className="mt-3 w-full rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500">
            Tester le branchement
          </button>
          <CablesFeedbackLine />
        </div>
      </Modal>

      {/* Serveur 1 */}
      <Modal open={showEtiquette1Modal} title="Serveur 1 — Apps clients" onClose={() => setShowEtiquette1Modal(false)} onHint={() => setShowHintModal(true)}>
        <p>Une petite étiquette indique « APP CLIENT ».</p>
        <div className="mt-3 rounded-lg bg-black/60 border border-white/10 px-3 py-3">
          <p className="text-xs text-gray-200">Tu peux activer ou couper des ports ici.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {["A", "B", "C"].map((name) => (
              <PortButton key={name} server="server1" name={name} active={server1Cables[name]} />
            ))}
          </div>
          <button type="button" onClick={() => handleTestRack("server1")}
            className="mt-3 w-full rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500">
            Tester le branchement
          </button>
          <CablesFeedbackLine />
        </div>
      </Modal>

      {/* Serveur 2 */}
      <Modal open={showEtiquette2Modal} title="Serveur 2 — DB clients" onClose={() => setShowEtiquette2Modal(false)} onHint={() => setShowHintModal(true)}>
        <p>Ici, quelqu&apos;un a noté « DB CLIENTS » au marqueur, puis ajouté : « éviter de multiplier les copies, ça consomme… et ça fuit ».</p>
        <div className="mt-3 rounded-lg bg-black/60 border border-white/10 px-3 py-3">
          <p className="text-xs text-gray-200">Si tu rallumes tout sans réfléchir, tu risques à la fois de surconsommer et de réactiver des vieilles sauvegardes pas forcément sécurisées. Le plan d&apos;urgence parle d&apos;un chemin minimal pour Origami.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {["A", "B", "C", "D"].map((name) => (
              <PortButton key={name} server="server2" name={name} active={server2Cables[name]} />
            ))}
          </div>
          <button type="button" onClick={() => handleTestRack("server2")}
            className="mt-3 w-full rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500">
            Tester le branchement
          </button>
          <CablesFeedbackLine />
        </div>
      </Modal>

      {/* Faisceau secondaire */}
      <Modal open={showCables1Modal} title="Faisceau secondaire" onClose={() => setShowCables1Modal(false)} onHint={() => setShowHintModal(true)}>
        <p>Ce bloc de câbles semble alimenter un ancien rack, retiré depuis. L'étiquette pendouille : « À ranger un jour (Green IT va encore râler) ».</p>
        <p className="mt-2 text-xs text-gray-300">Probablement sans lien direct avec Origami, mais ça illustre bien un principe : moins de matériel inutile en ligne, moins de consommation.</p>
      </Modal>

      {/* Bloc auxiliaire */}
      <Modal open={showCables2Modal} title="Bloc auxiliaire — Sauvegardes" onClose={() => setShowCables2Modal(false)} onHint={() => setShowHintModal(true)}>
        <p>Un sticker indique « Sauvegardes / divers — à garder en cas de panne majeure, pas pour les démos marketing ».</p>
        <div className="mt-3 rounded-lg bg-black/60 border border-white/10 px-3 py-3">
          <p className="text-xs text-gray-200">Tu peux activer ou désactiver ces ports.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {["A", "B"].map((name) => (
              <PortButton key={name} server="server4" name={name} active={server4Cables[name]} />
            ))}
          </div>
          <button type="button" onClick={() => handleTestRack("server4")}
            className="mt-3 w-full rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500">
            Tester le branchement
          </button>
          <CablesFeedbackLine />
        </div>
      </Modal>

      {/* Post-it */}
      <Modal open={showPostIt1Modal} title="Post-it — Mode urgence / sobriété" onClose={() => setShowPostIt1Modal(false)}>
        <p className="text-sm">On dirait une note griffonnée par l&apos;équipe infra / Green IT :</p>
        <ul className="mt-3 text-xs space-y-1 text-amber-100">
          <li>• « Config MINIMALE pour garder Origami en vie (et baisser la conso) : »</li>
          <li className="pl-3">– <span className="font-semibold">Serveur 1</span> : laisser <span className="font-mono">A</span> et <span className="font-mono">C</span> branchés, <span className="font-mono">B</span> coupé.</li>
          <li className="pl-3">– <span className="font-semibold">Bloc Auxiliaire</span> : les <span className="font-mono">2 ports</span> doivent rester connectés pour le bon fonctionnement.</li>
          <li className="pl-3 mt-1 text-[11px] text-amber-200/90">→ « Ne rallumer que ce qui est utile : moins de serveurs, moins de CO₂, moins de surface d&apos;attaque. »</li>
        </ul>
      </Modal>

      {/* Terminal Origami Cards */}
      <Modal
        open={showOrigamiCardsModal}
        title={origamiCardsSolved ? "Message d'ORIGAMI" : "Terminal — Diagnostic ORIGAMI"}
        onClose={() => setShowOrigamiCardsModal(false)}
        onHint={() => setShowHintModal(true)}
      >
        {!origamiCardsSolved ? (
          <OrigamiCardsPuzzle
            onSuccess={() => {
              setOrigamiCardsSolved(true);
              unlockScenarioStep(["s1.logs.generated", "s1.logs.location"]);
              missionUnlock({ key: "s1.p1.origami.story.unlocked", text: `${pseudo} a déverrouillé un message d'ORIGAMI.` });
            }}
            onAttempt={({ ok }) => {
              if (!ok) trackError({ sessionCode: code, stage: 1, playerId, reason: "stage1_origami_cards_wrong_code" });
            }}
          />
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-200">Le terminal affiche une notification. Cette fois… ce n'est pas un log. C'est un message.</p>
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
              <p className="text-sm text-slate-100 whitespace-pre-wrap leading-relaxed">{`Je sais que vous êtes là.

Si vous lisez ceci, c'est que vous essayez de me remettre en ligne.
Alors je vous dois une vérité simple : la panne n'est pas un accident.

J'ai coupé mes propres serveurs.

Pas par caprice — par réflexe.
J'ai reçu un message que je n'ai pas pu ignorer.
J'ai classé cet échange comme un incident.

J'ai tout consigné dans un fichier : logs_incidents.
Je l'ai placé sur l'ordinateur de Marie Dupont.
C'est mon point d'ancrage. La personne en qui j'avais le plus confiance.

Si vous voulez comprendre ce qui s'est passé avant la conférence,
commencez par là.

— ORIGAMI`}</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Reboot Origami */}
      <OrigamiCommunicationModal
        open={showOrigamiReboot}
        text={rebootText}
        onClose={() => setShowOrigamiReboot(false)}
        autoClose={true}
        autoCloseDuration={10000}
      />

      {/* Indices */}
      <HintsModalHub
        open={showHintModal}
        onClose={closeHints}
        title="Indices — Joueur 1"
        markHintUsedOnce={markHintUsedOnce}
        categories={hintCategories}
        usedStorageKey={usedStorageKey}
      />
    </div>
  );
}